import { z } from "zod";

export const AssistantListParamsSchema = z
  .object({
    minimal_response: z.boolean().default(true),
    scope: z.enum(["visible_to_user", "marketplace"]).default("visible_to_user"),
    page: z.number().default(0),
    per_page: z.number().default(12),
    filters: z.record(z.string(), z.unknown()).optional(),
  })
  .readonly();
export type AssistantListParams = Partial<z.infer<typeof AssistantListParamsSchema>>;

const PromptVariableSchema = z.object({
  key: z.string(),
  description: z.string().optional(),
  default_value: z.string(),
});

const MCPServerConfigObjectSchema = z.object({
  url: z.string().optional(),
  command: z.string().optional(),
  args: z.array(z.string()).optional(),
  env: z.record(z.string(), z.unknown()).optional(),
  auth_token: z.string().optional(),
  headers: z.record(z.string(), z.string()).optional(),
  type: z.string().optional(),
  single_usage: z.boolean().optional(),
  audience: z.string().optional(),
});

export const MCPServerConfigSchema = MCPServerConfigObjectSchema.optional().refine((config) => {
  if (!config) {
    return true;
  }
  const hasUrl = !!config.url;
  const hasCommand = !!config.command;
  return hasUrl !== hasCommand;
}, "Either 'url' or 'command' must be provided in config, but not both");

export const AssistantCreateParamsSchema = z
  .object({
    name: z.string(),
    description: z.string(),
    icon_url: z.string().optional(),
    system_prompt: z.string(),
    project: z.string(),
    context: z.array(
      z.object({
        context_type: z.enum(["knowledge_base", "code"]),
        name: z.string(),
      }),
    ),
    llm_model_type: z.string(),
    toolkits: z.array(
      z.object({
        toolkit: z.string(),
        tools: z.array(
          z.object({
            name: z.string(),
            label: z.string().optional(),
            settings_config: z.boolean(),
            user_description: z.string().optional(),
            settings: z.any().nullable().optional(),
          }),
        ),
        label: z.string(),
        settings_config: z.boolean(),
        is_external: z.boolean(),
        settings: z.any().optional(),
      }),
    ),
    conversation_starters: z.array(z.string()),
    shared: z.boolean().optional(),
    is_react: z.boolean().optional(),
    is_global: z.boolean().optional(),
    slug: z.string().optional(),
    temperature: z.number().optional(),
    top_p: z.number().optional(),
    mcp_servers: z.array(
      z.object({
        name: z.string(),
        description: z.string().optional(),
        enabled: z.boolean(),
        config: MCPServerConfigSchema,
        mcp_connect_url: z.string().optional(),
        tools_tokens_size_limit: z.number().optional(),
        command: z.string().optional(),
        arguments: z.string().optional(),
        settings: z.any().optional(),
        mcp_connect_auth_token: z.any().optional(),
      }),
    ),
    assistant_ids: z.array(z.string()),
    skill_ids: z.array(z.string()).optional(),
    prompt_variables: z.array(PromptVariableSchema).optional().default([]),
    categories: z.array(z.string()).max(3).optional(),
    skip_integration_validation: z.boolean().optional().default(false),
  })
  .readonly();
// In order to make sure that default values are applied correctly, we use z.input here
// instead of z.infer which would give us the type after defaults are applied.
export type AssistantCreateParams = z.input<typeof AssistantCreateParamsSchema>;

export const AssistantUpdateParamsSchema = AssistantCreateParamsSchema;
export type AssistantUpdateParams = z.input<typeof AssistantUpdateParamsSchema>;

export const AssistantChatParamsSchema = z
  .object({
    conversation_id: z.string().optional(),
    text: z.string(),
    content_raw: z.string().optional(),
    file_names: z.array(z.string()).optional(),
    llm_model: z.string().optional(),
    history: z.union([
      z.array(
        z.object({
          role: z.enum(["Assistant", "User"]),
          message: z.string().optional(),
        }),
      ),
      z.string(),
    ]),
    history_index: z.number().optional(),
    stream: z.boolean().optional(),
    propagate_headers: z.boolean().optional(),
    custom_metadata: z.record(z.string(), z.unknown()).optional(),
    top_k: z.number().optional(),
    system_prompt: z.string().optional(),
    background_task: z.boolean().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    mcp_server_single_usage: z.boolean().optional(),
    save_history: z.boolean().optional(),

    version: z.number().optional(),
    output_schema: z
      .union([z.custom((val) => val instanceof z.ZodType), z.record(z.string(), z.unknown())])
      .optional(),
  })
  .readonly();
export type AssistantChatParams = z.infer<typeof AssistantChatParamsSchema>;

const VirtualAssistantContextSchema = z.object({
  context_type: z.enum(["knowledge_base", "code"]),
  name: z.string(),
});

const VirtualAssistantToolSchema = z.object({
  name: z.string(),
  label: z.string().nullable().optional(),
  settings_config: z.boolean(),
  user_description: z.string().nullable().optional(),
  settings: z.any().nullable().optional(),
});

const VirtualAssistantToolKitSchema = z.object({
  toolkit: z.string(),
  tools: z.array(VirtualAssistantToolSchema),
  label: z.string(),
  settings_config: z.boolean(),
  is_external: z.boolean().nullable().optional(),
  settings: z.any().optional(),
});

const VirtualAssistantMCPServerConfigSchema = z.object({
  command: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  args: z.array(z.string()).nullable().optional(),
  env: z.record(z.string(), z.unknown()).nullable().optional(),
  auth_token: z.string().nullable().optional(),
  headers: z.record(z.string(), z.string()).nullable().optional(),
  type: z.string().nullable().optional(),
  single_usage: z.boolean().nullable().optional(),
  audience: z.string().nullable().optional(),
  auth_config: z.record(z.string(), z.unknown()).nullable().optional(),
  tools: z.array(z.string()).nullable().optional(),
});

const VirtualAssistantMCPServerSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
  enabled: z.boolean(),
  config: VirtualAssistantMCPServerConfigSchema.optional(),
  mcp_connect_url: z.string().nullable().optional(),
  tools_tokens_size_limit: z.number().nullable().optional(),
  command: z.string().nullable().optional(),
  arguments: z.string().nullable().optional(),
  settings: z.any().optional(),
  mcp_connect_auth_token: z.any().optional(),
});

export const VirtualAssistantChatParamsSchema = z
  .object({
    // Assistant definition fields
    system_prompt: z.string().default(""),
    llm_model_type: z.string().optional(),
    temperature: z.number().min(0).max(2).optional(),
    top_p: z.number().min(0).max(1).optional(),
    toolkits: z.array(VirtualAssistantToolKitSchema).default([]),
    context: z.array(VirtualAssistantContextSchema).default([]),
    mcp_servers: z.array(VirtualAssistantMCPServerSchema).default([]),
    skill_ids: z.array(z.string()).default([]),
    assistant_ids: z.array(z.string()).default([]),
    agent_mode: z.enum(["general", "plan_execute"]).default("general"),
    plan_prompt: z.string().optional(),
    smart_tool_selection_enabled: z.boolean().default(false),
    prompt_variables: z.array(PromptVariableSchema).default([]),

    // Chat parameters
    conversation_id: z.string().optional(),
    text: z.string(),
    content_raw: z.string().optional(),
    file_names: z.array(z.string()).optional(),
    history: z
      .union([
        z.array(
          z.object({
            role: z.enum(["Assistant", "User"]),
            message: z.string().optional(),
          }),
        ),
        z.string(),
      ])
      .default([]),
    stream: z.boolean().optional(),
    output_schema: z
      .union([z.custom((val) => val instanceof z.ZodType), z.record(z.string(), z.unknown())])
      .optional(),
    tools_config: z
      .array(
        z.object({
          name: z.string(),
          tool_creds: z.record(z.string(), z.unknown()).optional(),
          integration_id: z.string().optional(),
        }),
      )
      .optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    top_k: z.number().default(10),
    propagate_headers: z.boolean().default(false),
    disable_cache: z.boolean().optional(),
    mcp_server_single_usage: z.boolean().optional(),
  })
  .readonly();
export type VirtualAssistantChatParams = z.input<typeof VirtualAssistantChatParamsSchema>;

export const AssistantEvaluationParamsSchema = z
  .object({
    dataset_id: z.string(),
    experiment_name: z.string(),
    system_prompt: z.string().optional(),
    llm_model: z.string().optional(),
  })
  .readonly();
export type AssistantEvaluationParams = z.infer<typeof AssistantEvaluationParamsSchema>;

export const AssistantVersionsListParamsSchema = z
  .object({
    page: z.number().optional(),
    per_page: z.number().optional(),
  })
  .readonly();
export type AssistantVersionsListParams = Partial<
  z.infer<typeof AssistantVersionsListParamsSchema>
>;
